/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type {
  MutationOptions,
  QueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { omit } from 'lodash';
import type { Credentials } from '@opengeoweb/authentication';
import { useAuthenticationContext } from '@opengeoweb/authentication';

export const getHeaders = (auth?: Credentials): HeadersInit =>
  omit(
    {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${auth?.token}`,
      Accept: 'application/json',
    },
    auth ? [] : ['Authorization'],
  );

export const withJsonFormat = (url: string): string => {
  const urlObj = new URL(url);
  urlObj.searchParams.set('f', 'json');
  return urlObj.toString();
};

// This function has a copy in libs/webmap-react/src/lib/edr-layer-api/api.ts. This cannot be used there due to circular dependency
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const handleResponse = async (response: Response): Promise<any> => {
  if (response.ok) {
    return response
      .json()
      .then((json) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return json ?? response.headers;
      })
      .catch(() => {
        // No JSON response
      });
  }
  return response
    .json()
    .catch(() => {
      // Couldn't parse the JSON
      throw new Error(`${response.status}`);
    })
    .then((json) => {
      // Got valid JSON with error response, use it
      if (json) {
        const { message } = json;
        if (message) {
          throw new Error(message, { cause: response.status });
        }
        const result = JSON.stringify(json);
        throw new Error(result);
      }
      throw new Error(`${response.status}`);
    });
};

export const useAuthQuery = <T>(
  queryKey: string[],
  queryFn: (auth: Credentials) => Promise<T>,
  disabled = false,
  refetchInterval: number | false = false,
): UseQueryResult<T> => {
  const { auth } = useAuthenticationContext();
  const enabled = !!auth && !disabled;
  return useQuery({
    queryKey,
    queryFn: async (): Promise<T> => queryFn(auth!),
    enabled,
    refetchInterval,
  });
};

export const optimisticUpdate = <
  U extends { id?: string },
  T extends { id?: string },
>(
  updatedQueryKey: string[],
  queryClient: QueryClient,
  optimisticUpdate: (oldValue: U, variables: T) => U,
  errorCallback?: (err: Error) => void,
): MutationOptions<Headers | null, Error, T, { oldData: U[] | undefined }> => ({
  onMutate: async (variables: T): Promise<{ oldData: U[] | undefined }> => {
    void queryClient.cancelQueries({ queryKey: updatedQueryKey });
    const oldData = queryClient.getQueryData<U[]>(updatedQueryKey);
    if (variables.id) {
      queryClient.setQueryData<U[]>(updatedQueryKey, (old) =>
        old?.map((item) =>
          item.id === variables.id ? optimisticUpdate(item, variables) : item,
        ),
      );
    }
    return { oldData };
  },
  onError: (err: Error, _: T, context?: { oldData: U[] | undefined }): void => {
    if (context?.oldData) {
      queryClient.setQueryData(updatedQueryKey, context?.oldData);
    }
    if (errorCallback) {
      errorCallback(err);
    }
  },
});
